import React from 'react';
import { visibleParts, TypeCycleState } from './heroTypeCycle';
import type { HeroSlide } from './heroSlides';

type HeroHeadlineProps = {
  slides: HeroSlide[];
  cycle: TypeCycleState;
  reduced: boolean;
};

// The typed headline. The cycle comes from useHeroCycle in Hero, shared with the line under it (WU-HERO).
const HeroHeadline: React.FC<HeroHeadlineProps> = ({ slides, cycle, reduced }) => {
  const slideIndex = reduced ? 0 : cycle.phraseIndex;
  const slide = slides[slideIndex] ?? slides[0];
  const full = slide.prefix + slide.accent + slide.suffix;
  const charCount = reduced ? full.length : cycle.charCount;
  const parts = visibleParts(slide.prefix, slide.accent, slide.suffix, charCount);
  const showCaret = !reduced && !(cycle.charCount === 0 && cycle.phase === 'gap');
  // Screen readers and crawlers always get slide 1, so nothing is announced as the text changes.
  const first = slides[0];

  return (
    <h1
      className="hero-headline"
      style={{
        fontSize: 'var(--text-display)',
        fontWeight: 700,
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      {/* Invisible full phrases share the typed text's grid cell, so the headline keeps the height of the
          longest phrase while it types. Without them the page below jumps (up to 153 px at 1280). */}
      {slides.map((ghost) => (
        <span key={ghost.key} className="hero-headline__ghost" aria-hidden="true">
          {ghost.prefix + ghost.accent + ghost.suffix}
          <span className="hero-caret" />
        </span>
      ))}
      <span aria-hidden="true">
        {parts.prefix}
        <span style={{ color: 'var(--mint)' }}>{parts.accent}</span>
        {parts.suffix}
        {showCaret ? <span className="hero-caret" aria-hidden="true" /> : null}
      </span>
      <span className="sr-only">{first.prefix + first.accent + first.suffix}</span>
    </h1>
  );
};

export default HeroHeadline;
